import React from 'react';
import { Helmet } from 'react-helmet'; // SEO Added
import { Hammer, HardHat, BrickWall, Truck, CheckSquare, Ruler, ShieldCheck, Clock, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import img1 from "../../assets/builders1.jpg";
import img2 from "../../assets/builders2.jpg";
import img3 from "../../assets/builders3.jpg";
import img4 from "../../assets/builders4.jpg";
import img5 from "../../assets/builders5.jpg";

const projects = [
  { 
    id: 1, 
    title: 'Modern Villa Construction', 
    category: 'Turnkey Residential', 
    image: img1,
    desc: '500 sq. yard luxury villa in Noida with swimming pool and landscaping.'
  },
  { 
    id: 2, 
    title: 'Commercial High-Rise', 
    category: 'Steel Structure', 
    image: img2,
    desc: 'G+4 commercial complex with glass facade and basement parking in Lucknow.'
  },
  { 
    id: 3, 
    title: 'Heritage Restoration', 
    category: 'Renovation', 
    image: img3,
    desc: 'Restoring structural integrity of old havelis in Varanasi while preserving aesthetics.'
  },
  { 
    id: 4, 
    title: 'Structural Engineering', 
    category: 'Technical', 
    image: img4,
    desc: 'Heavy foundation work and retaining walls for industrial requirements.'
  },
  { 
    id: 5, 
    title: 'Farmhouse Development', 
    category: 'Landscape & Build', 
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80",
    desc: 'Eco-friendly construction with rain water harvesting and solar integration.'
  },
  { 
    id: 6, 
    title: 'Grey Structure Services', 
    category: 'Core Build', 
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&q=80",
    desc: 'Brickwork, plastering, lintel casting, and electrical piping with premium cement.'
  },
];

const LuxuryBuilders = () => {
  // SEO Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "General Contractor",
    "provider": {
      "@type": "Organization",
      "name": "Luxuriya Homes"
    },
    "areaServed": ["Noida", "Delhi", "Lucknow", "Varanasi"],
    "description": "Turnkey home construction, grey structure services, and civil contracting."
  };

  return (
    <div className="font-sans text-gray-800 bg-white">
      
      {/* SEO HEAD */}
      <Helmet>
        <title>Luxury Home Builders in Noida, Delhi & Lucknow | Turnkey Construction</title>
        <meta name="description" content="Premium construction company for villas and high-rises. We offer grey structure and turnkey construction services with 100% quality assurance in Delhi NCR & UP." />
        <meta name="keywords" content="luxury builders in noida, home construction company lucknow, turnkey contractors delhi, civil contractors varanasi, villa construction cost" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      {/* 1. HERO SECTION */}
      <div
        className="relative h-[75vh] w-full bg-cover bg-center"
        style={{ backgroundImage: `url(${img5})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 animate-fadeIn">
          <h4 className="text-amber-500 font-bold tracking-[0.3em] uppercase mb-3">
             Master Craftsmanship
          </h4>
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
             Luxury Builders
          </h1>
          <p className="text-gray-200 text-lg md:text-xl max-w-xl font-light leading-relaxed">
             Building legacies with concrete, steel, and passion in <strong>Delhi NCR </strong>.
          </p>
        </div>
      </div>

      {/* 2. INTRO SECTION */}
      <section className="py-24 px-6 bg-white text-center">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
           Foundations of Excellence
        </h2>
        <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
        <p className="text-gray-600 max-w-4xl mx-auto leading-loose text-lg">
          We don't just build houses; we construct landmarks. As premium builders in Noida and Lucknow, we manage the entire construction lifecycle. 
          From soil testing and structural design to the final coat of weather-proof paint, we ensure structural integrity and flawless finishing.
        </p>
      </section>

      {/* 3. EXPANDED GALLERY */}
      <section className="py-20 bg-neutral-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
             <h3 className="text-4xl font-serif font-bold text-gray-900 mb-2">Construction Portfolio</h3>
             <p className="text-gray-500">Delivering Quality Since 2010</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((item) => (
              <div key={item.id} className="group relative overflow-hidden shadow-xl cursor-pointer rounded-sm bg-white hover:-translate-y-2 transition-transform duration-300">
                <div className="h-72 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={`${item.title} - Construction Project`} 
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700"
                  />
                </div>
                <div className="p-8 border-b-4 border-transparent group-hover:border-amber-500 transition-all">
                  <p className="text-amber-600 text-xs tracking-widest uppercase mb-2 font-bold">{item.category}</p>
                  <h4 className="text-xl font-serif text-gray-900 mb-3">{item.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. PROCESS & QUALITY (ICONS) */}
      <section className="py-24 px-6 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto">
           <div className="text-center mb-16">
              <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4">The Gold Standard</h3>
              <p className="text-gray-400">Strict adherence to NBC (National Building Code).</p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
              <div className="flex flex-col items-center">
                <HardHat className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Expert Team</h4>
                <p className="text-gray-400 leading-relaxed">
                   Civil engineers and architects with decades of experience supervise every casting and curing process.
                </p>
              </div>
              <div className="flex flex-col items-center">
                <BrickWall className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Premium Materials</h4>
                <p className="text-gray-400 leading-relaxed">
                   Sourcing only Ultratech/Ambuja Cement and Tata Tiscon Steel for maximum longevity.
                </p>
              </div>
              <div className="flex flex-col items-center">
                <Hammer className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Timely Delivery</h4>
                <p className="text-gray-400 leading-relaxed">
                   We use Project Management software to track timelines and ensure handover on the promised date.
                </p>
              </div>
              
              {/* Row 2 */}
              <div className="flex flex-col items-center mt-8">
                <Truck className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Material Testing</h4>
                <p className="text-gray-400 leading-relaxed">On-site cube testing for concrete strength and regular brick quality checks.</p>
              </div>
              <div className="flex flex-col items-center mt-8">
                <CheckSquare className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Quality Audits</h4>
                <p className="text-gray-400 leading-relaxed">Weekly inspections by senior engineers to check plastering levels and waterproofing.</p>
              </div>
              <div className="flex flex-col items-center mt-8">
                <Ruler className="w-14 h-14 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Zero Deviation</h4>
                <p className="text-gray-400 leading-relaxed">Construction strictly as per the sanctioned map to avoid authority penalties later.</p>
              </div>
           </div>
        </div>
      </section>

      {/* 5. SERVICES COMPARISON (NEW) */}
      <section className="py-24 px-6 bg-white">
         <div className="max-w-5xl mx-auto">
            <h3 className="text-3xl font-serif font-bold text-center mb-12">Our Construction Packages</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
               {/* Grey Structure */}
               <div className="border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                     <Layers size={32} className="text-gray-600"/>
                  </div>
                  <h4 className="text-2xl font-bold mb-4">Grey Structure</h4>
                  <ul className="space-y-3 text-gray-600 mb-8">
                     <li className="flex gap-2"><CheckSquare size={18} className="text-green-600"/> Foundation & RCC Structure</li>
                     <li className="flex gap-2"><CheckSquare size={18} className="text-green-600"/> Brickwork & Plastering</li>
                     <li className="flex gap-2"><CheckSquare size={18} className="text-green-600"/> Electrical Piping & Plumbing Lines</li>
                     <li className="flex gap-2"><CheckSquare size={18} className="text-green-600"/> Waterproofing</li>
                  </ul>
                  <Link to="/contact" className="block text-center w-full border border-gray-900 py-3 font-bold hover:bg-gray-900 hover:text-white transition-colors">Get Estimate</Link>
               </div>

               {/* Turnkey */}
               <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-8 hover:shadow-xl transition-shadow relative overflow-hidden">
                  <div className="absolute top-0 right-0 bg-amber-500 text-white px-4 py-1 text-xs font-bold uppercase">Popular</div>
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                     <ShieldCheck size={32} className="text-amber-600"/>
                  </div>
                  <h4 className="text-2xl font-bold mb-4">Turnkey Solution</h4>
                  <ul className="space-y-3 text-gray-600 mb-8">
                     <li className="flex gap-2"><CheckSquare size={18} className="text-green-600"/> <strong>Everything in Grey Structure</strong></li>
                     <li className="flex gap-2"><CheckSquare size={18} className="text-green-600"/> Flooring & Tiling</li>
                     <li className="flex gap-2"><CheckSquare size={18} className="text-green-600"/> Paint & Finishing</li>
                     <li className="flex gap-2"><CheckSquare size={18} className="text-green-600"/> Door/Window Installation</li>
                  </ul>
                  <Link to="/contact" className="block text-center w-full bg-amber-600 text-white py-3 font-bold hover:bg-amber-700 transition-colors">Get Premium Quote</Link>
               </div>
            </div>
         </div>
      </section>

      {/* 6. CTA */}
      <section className="py-24 px-6 text-center bg-white border-t border-gray-100">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
           Start Building Your Dream
        </h2>
        <p className="text-gray-600 mb-10 text-lg max-w-2xl mx-auto">
           Get a detailed construction cost estimate for your plot in Delhi NCR or UP.
        </p>
        <Link to="/contact" className="inline-block bg-amber-500 hover:bg-amber-600 text-white text-lg font-bold px-10 py-4 rounded-sm transition shadow-lg uppercase tracking-widest">
           Request Construction Quote
        </Link>
      </section>
    </div>
  );
};

export default LuxuryBuilders;